import isEmpty from 'lodash/isEmpty';
import moment from 'moment-timezone';
import { DateHelper } from './DateUtil';
import { safeArrayAccess } from './safeArrayAccess';

const moment_fullDate = 'DD MMM YYYY, hh:mm A';
export const moment_fullDate24H = 'DD MMM YYYY, HH:mm:ss';
export const moment_dateOnly = 'D MMMM YYYY';
const moment_24H = 'HH:mm:ss';

export const dinaMonthStrings: Record<number, string> = {
  0: 'January',
  1: 'February',
  2: 'March',
  3: 'April',
  4: 'May',
  5: 'June',
  6: 'July',
  7: 'August',
  8: 'September',
  9: 'October',
  10: 'November',
  11: 'December',
};

/**
 * Returns whether the given date is valid
 */
export const isValidDate = (date: Date | string | undefined): boolean => {
  if (!date) return false;
  return moment(date, moment.ISO_8601, true).isValid();
};

/**
 * Converts a UTC date string to readable local date string
 * @param utcDateString
 */
export const formatDate = (
  utcDateString: string,
  formatString = moment_fullDate
) =>
  isEmpty(utcDateString)
    ? ''
    : moment(new Date(utcDateString)).format(formatString);

/**
 * Converts Miliseconds to time code (Without the frame number)
 * @param milliseconds
 */
export const millisecondsToTimecode = (milliseconds: number) => {
  const time = moment.duration(milliseconds);
  const hours = Math.trunc(time.asHours());
  const minutes = time.minutes();
  const seconds = time.seconds();
  return `${hours.toString().padStart(2, '0')} : ${minutes
    .toString()
    .padStart(2, '0')} : ${seconds.toString().padStart(2, '0')}`;
};

/**
 * Converts milliseconds to a human readable time, youtube style
 * @param milliseconds
 */
export const millisecondsToHumanReadableTime = (
  milliseconds: number | undefined | null
) => {
  if (!milliseconds) {
    return '0:00';
  }

  const time = moment.duration(milliseconds);
  return formatHours(time) + formatMinutes(time) + formatSeconds(time);
};

export function msToReadableTime(ms: number | undefined | null) {
  return ms ? millisecondsToHumanReadableTime(ms) : '0:00';
}

export const getFullDateStringOfUtcFromDateMs = (dateMs: number): string => {
  return moment.utc(dateMs).format(moment_fullDate24H);
};

export const getHhMmSsOfUtcFromDateMs = (dateMs: number): string => {
  return moment.utc(dateMs).format(moment_24H);
};

export const getHhMmSsFromDateMs = (dateMs: number): string => {
  return new DateHelper().format(dateMs, { format: 'Timecode' });
};

const formatHours = (time: any) => {
  const hours = Math.trunc(time.asHours());
  return hours > 0 ? `${hours}:` : '';
};

const formatMinutes = (time: moment.Duration) => {
  const hours = Math.trunc(time.asHours());
  return hours > 0
    ? `${String(time.minutes()).padStart(2, '0')}:`
    : `${time.minutes()}:`;
};

const formatSeconds = (time: moment.Duration) => {
  return time.seconds() > 0
    ? `${String(time.seconds()).padStart(2, '0')}`
    : '00';
};

export const humanReadableTimeToMilliseconds = (hrt: string) => {
  const [seconds = '0', minutes = '0', hours = '0'] = hrt.split(':').reverse();
  const parsedMinutes = parseInt(minutes);
  const parsedSeconds = parseInt(seconds);
  if (parsedMinutes > 59 || parsedSeconds > 59) return;

  return (
    parsedSeconds * 1000 +
    parsedMinutes * 60000 +
    parseInt(hours) * 60 * 60 * 1000
  );
};

/**
 * Compares 2 dates to see if they are on the same day for the timezone
 */
export const isSameDay = (
  d1: Date,
  d2: Date,
  timezone: string | undefined
): boolean => {
  const dateHelper = new DateHelper(timezone);
  const m1 = dateHelper.date(d1.toISOString());
  const m2 = dateHelper.date(d2.toISOString());
  return (
    m1.year() === m2.year() &&
    m1.month() === m2.month() &&
    m1.date() === m2.date()
  );
};

/**
 * Returns the amount of days for the given year and month. This
 * takes into account leap years as well. Month parameter
 * starts at index 0 to 11.
 */
export const getDaysInMonth = (year: number, month: number): number => {
  return new Date(year, month + 1, 0).getDate();
};

/**
 * Get the week number of the year corresponding to the given date.
 * Taken from: https://weeknumber.com/how-to/javascript
 */
export const getWeekNumber = (dateStr?: string): number => {
  let date: Date;
  if (!dateStr) {
    date = new Date();
  } else {
    date = new Date(dateStr);
  }
  date.setHours(0, 0, 0, 0);
  // Thursday in current week decides the year.
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  // January 4 is always in week 1.
  const week1 = new Date(date.getFullYear(), 0, 4);
  // Adjust to Thursday in week 1 and count number of weeks from date to week1.
  return (
    1 +
    Math.round(
      ((date.getTime() - week1.getTime()) / 86400000 -
        3 +
        ((week1.getDay() + 6) % 7)) /
        7
    )
  );
};

/**
 * Given a duration in milliseconds, produce a timecode in HH:mm:ss style,
 * but where the hour does not wrap around at 24.
 */
export const msToHourAndMins = (
  durationInMs: number,
  includeSeconds = false,
  prettify = false
): string => {
  const seconds = Math.floor((durationInMs / 1000) % 60);
  const minutes = Math.floor((durationInMs / (1000 * 60)) % 60);
  const hours = Math.floor(durationInMs / (1000 * 60 * 60));
  const hrString = hours < 10 ? '0' + hours : hours;
  const minuteString = minutes < 10 ? '0' + minutes : minutes;
  const secondString = seconds < 10 ? '0' + seconds : seconds;

  if (prettify) {
    const prettifiedHoursAndMinutes = `${hrString}h ${minuteString}m`;
    return includeSeconds
      ? `${prettifiedHoursAndMinutes} ${secondString}s`
      : prettifiedHoursAndMinutes;
  }

  const hourMinutesString = `${hrString}:${minuteString}`;
  return includeSeconds
    ? `${hourMinutesString}:${secondString}`
    : hourMinutesString;
};

/**
 * Compares 2 dates to see if they range from january 1st to december 31st for the timezone
 */
export const isFullYear = (
  d1: Date,
  d2: Date,
  timezone: string | undefined
): boolean => {
  const dateHelper = new DateHelper(timezone);
  const m1 = dateHelper.date(d1.toISOString());
  const m2 = dateHelper.date(d2.toISOString());

  if (m1.year() !== m2.year()) return false;
  if (m1.month() !== 0 || m1.date() !== 1) return false;
  const daysInMonth = getDaysInMonth(m2.year(), m2.month());
  if (m2.month() !== 11 || m2.date() !== daysInMonth) return false;

  return true;
};

export const isSameMonth = (
  d1: Date,
  d2: Date,
  timezone: string | undefined
): boolean => {
  const dateHelper = new DateHelper(timezone);
  const m1 = dateHelper.date(d1.toISOString());
  const m2 = dateHelper.date(d2.toISOString());
  return m1.year() === m2.year() && m1.month() === m2.month();
};

/**
 * Compares to dates to see if they range from the start to the end of
 * the same month for the timezone
 */
export const isFullMonth = (
  d1: Date,
  d2: Date,
  timezone: string | undefined
): boolean => {
  const dateHelper = new DateHelper(timezone);
  const m1 = dateHelper.date(d1.toISOString());
  const m2 = dateHelper.date(d2.toISOString());
  if (m1.month() !== m2.month()) return false;
  const daysInMonth = getDaysInMonth(m2.year(), m2.month());
  if (m1.date() !== 1 || m2.date() !== daysInMonth) return false;
  return true;
};

export function isUTCMidnight(date: Date) {
  if (
    date.getUTCHours() ||
    date.getUTCMinutes() ||
    date.getUTCSeconds() ||
    date.getUTCMilliseconds()
  ) {
    return false;
  }

  return true;
}

export function roundUpToUTCMidnight(mutDate: Date) {
  const hours = isUTCMidnight(mutDate) ? 0 : 24;
  mutDate.setUTCHours(hours, 0, 0, 0);
}

export function roundDownToUTCMidnight(mutDate: Date) {
  mutDate.setUTCHours(0, 0, 0, 0);
}

export function addDaysAndRoundUpToUTCMidnight(mutDate: Date, days: number) {
  mutDate.setUTCDate(mutDate.getUTCDate() + days);
  roundUpToUTCMidnight(mutDate);
}

/**
 * Formats time by considering timezone.
 */
export function formatTimeInTimezone(
  timeStamp: number,
  timeZone: string,
  format: string
) {
  return moment.tz(timeStamp, timeZone).format(format);
}

export function formatTimecodeInTimeZoneDynamic(
  timeStamp: number,
  timeZone: string,
  timeFormat: string
) {
  return new DateHelper().format(timeStamp, {
    format: 'TimecodeShort',
    timeZone,
    hour12: timeFormat !== '24h',
  });
}

export function formatDateInTimeZone(timeStamp: number, timeZone: string) {
  return new DateHelper().format(timeStamp, {
    format: 'DayMonthYear',
    timeZone,
  });
}

export function formatISODateInTimeZone(timeStamp: number, timeZone: string) {
  return new DateHelper().format(timeStamp, { format: 'ISO', timeZone });
}

export function formatDateForTooltip(
  timestamp: number,
  timeZone: string,
  timeFormat: string
) {
  return new DateHelper().format(timestamp, {
    format: 'ShortDayMonthYearHourMinutes',
    timeZone,
    hour12: timeFormat !== '24h',
  });
}

export function formatTimeInTimeZone(
  timeStamp: number,
  timeZone: string,
  timeFormat: string
) {
  return new DateHelper().format(timeStamp, {
    format: 'HourMinuteSeconds',
    timeZone,
    hour12: timeFormat !== '24h',
  });
}

/**
 * Returns utc offset of given time in given timezone.
 */
export function getUtcOffsetInMs(timeStamp: number, timeZone: string) {
  return moment.tz(timeStamp, timeZone).utcOffset() * 60 * 1000;
}

/**
 * Determines if a date of a timezone is affected by DST changeover.
 */
export function localTimeMayBeAmbiguous(date: Date, timeZone: string) {
  const oneHour = 60 * 60 * 1000;
  const time = date.getTime();
  return (
    moment.tz(time + oneHour, timeZone).utcOffset() !=
    moment.tz(time - oneHour, timeZone).utcOffset()
  );
}

export function minutesToHhMm(input: number) {
  const isNegative = input < 0;
  const num = Math.abs(input);
  const tempHours = num / 60;
  const hours = Math.floor(tempHours);
  const tempMinutes = (tempHours - hours) * 60;
  const minutes = Math.round(tempMinutes);
  return isNegative ? [-hours, -minutes] : [hours, minutes];
}

/**
 * Converts input hours : minutes (UTC) to timezone hours : minutes.
 */
export function evaluateTimeFromUtcOffsetAndInputTime(
  offset: number,
  hh: number,
  mm: number
) {
  const result = minutesToHhMm(offset / (60 * 1000));
  let hours = hh + safeArrayAccess(result, 0);
  let minutes = mm + safeArrayAccess(result, 1);
  if (minutes > 59) {
    minutes %= 60;
    hours += minutes / 60;
  }
  // When Hours is greater than 23, subtract by 24.
  if (hours > 23) {
    hours -= 24;
  }
  // When hours is negative value, subtract from 24.
  if (hours < 0) {
    hours = 24 - Math.abs(hours);
  }
  return [hours, minutes];
}

/**
 * Returns timestamp of input date and time.
 */
export function getTimestampOfDateTime(
  date: Date,
  time: string,
  timezone: string
) {
  const formattedDate = formatTimeInTimezone(
    date.getTime(),
    timezone,
    'DD/MM/YYYY'
  );
  const tempDate = `${formattedDate} ${time}`;
  // unix() returns timestamp in seconds.
  return moment.tz(tempDate, 'DD/MM/YYYY HH:mm', timezone).unix() * 1000;
}

/**
 * Returns utc timestamp of start of the day : 1:00 am'.
 */
export function getSchedulerStartOfDayTimestamp(
  timestamp: number,
  timezone: string
) {
  // Check PR #11384 for explanation.
  return moment.tz(timestamp, timezone).startOf('day').hour(1).unix() * 1000;
}

const CALENDAR_DAY_FORMAT = 'YYYY-MM-DD';

/**
 * Returns the 'YYYY-MM-DD' day the given instant falls on in the timezone.
 */
export function toCalendarDay(timestamp: number, timezone: string): string {
  return moment.tz(timestamp, timezone).format(CALENDAR_DAY_FORMAT);
}

/**
 * Returns the first and last millisecond of a 'YYYY-MM-DD' day in the timezone.
 * Deriving both boundaries from the day handles 23- and 25-hour DST transition days.
 */
export function getCalendarDayRange(day: string, timezone: string): { start: number, end: number } {
  const date = moment.tz(day, CALENDAR_DAY_FORMAT, timezone);
  return {
    start: date.clone().startOf('day').valueOf(),
    end: date.clone().endOf('day').valueOf(),
  };
}

/**
 * Returns relative time difference between given date and current date in human readable words (e.g. 2 days ago).
 * @param dateString - Valid date string.
 */
export function getReadableTimeFromNow(dateString: string) {
  return moment(dateString).fromNow();
}
